import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import strings from 'common/Translation/Translate';
import { isOperaIntegration, PageProps } from '../../../utils/constants/data';
import { routes } from '../../../utils/constants/routes';
import { selectIntegration, User } from 'utils/redux/reducer/authentication-slice';
import { URLS } from '../../../utils/constants/urls';
import ListPage from '../CommonPage';
import { buildCicAndFilter, HeaderFilterTextField } from '../CommonPage/headerFilterField';
import { pageNo, rowCount, setPageNo } from 'utils/redux/reducer/filter-slice';
import axiosInstance from '../../../utils/axios';
import { ToolTip } from '../../../components/Tooltip/ToolTip';

const OPERA_LOG_TARGET = 'OPERA';

const EMPTY_FILTERS = {
  traceId: '',
  method: '',
  hostname: '',
  url: '',
  responseStatus: '',
};

const FILTER_CHIP_LABELS: Record<keyof typeof EMPTY_FILTERS, string> = {
  traceId: 'Trace ID',
  method: 'Method',
  hostname: 'Hostname',
  url: 'URL',
  responseStatus: 'Status',
};

const truncate = (text: string | null | undefined, max = 80) => {
  if (text == null || text === '') return '—';
  return text.length <= max ? text : `${text.slice(0, max)}…`;
};

const parseHttpStatus = (val: unknown): number | null => {
  if (val == null || val === '') {
    return null;
  }
  const n = typeof val === 'number' ? val : parseInt(String(val), 10);
  return Number.isFinite(n) ? n : null;
};

const formatBodyForDisplay = (raw: string | null | undefined): string => {
  if (raw == null || raw.trim() === '') {
    return '(empty)';
  }
  const trimmed = raw.trim();
  try {
    return JSON.stringify(JSON.parse(trimmed), null, 2);
  } catch {
    return raw;
  }
};

const OperaLogsList: FC = () => {
  const dispatch = useDispatch();
  const index = useSelector(pageNo);
  const pageSize = useSelector(rowCount);
  const user = useSelector(User) as { role?: { value?: string } } | null;
  const integration = useSelector(selectIntegration);
  const canViewOperaLogs = user?.role?.value === 'ROLE_BBR_ADMIN' && isOperaIntegration(integration);

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [pageResponse, setPageResponse] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [bodyDialog, setBodyDialog] = useState<{ open: boolean; title: string; text: string }>({
    open: false,
    title: '',
    text: '',
  });

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setAppliedFilters(prev =>
        Object.keys(filters).every(key => prev[key as keyof typeof EMPTY_FILTERS] === filters[key as keyof typeof EMPTY_FILTERS])
          ? prev
          : filters,
      );
      dispatch(setPageNo(0));
    }, 350);
    return () => window.clearTimeout(handle);
  }, [dispatch, filters]);

  const updateFilter = (field: keyof typeof EMPTY_FILTERS, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const openBodyDialog = (title: string, raw: string | null | undefined) => {
    setBodyDialog({ open: true, title, text: formatBodyForDisplay(raw) });
  };

  const closeBodyDialog = () => {
    setBodyDialog(prev => ({ ...prev, open: false }));
  };

  const copyBodyText = () => {
    if (bodyDialog.text && bodyDialog.text !== '(empty)') {
      void navigator.clipboard.writeText(bodyDialog.text);
    }
  };

  const pageConfig = useMemo(
    () => ({
      title: strings.menu_opera_logs,
      subtitle: 'Review Opera request and response traffic.',
      entityLabel: 'Logs',
      hideActionText: true,
      headerFilters: () => (
        <>
          <HeaderFilterTextField
            label="Trace ID"
            placeholder="Exact match"
            value={filters.traceId}
            onChange={value => updateFilter('traceId', value)}
          />
          <HeaderFilterTextField
            label="Method"
            placeholder="e.g. POST"
            value={filters.method}
            onChange={value => updateFilter('method', value)}
          />
          <HeaderFilterTextField
            label="Hostname"
            placeholder="Exact match"
            value={filters.hostname}
            onChange={value => updateFilter('hostname', value)}
          />
          <HeaderFilterTextField
            label="URL"
            placeholder="Contains"
            value={filters.url}
            onChange={value => updateFilter('url', value)}
          />
          <HeaderFilterTextField
            label="Status"
            placeholder="e.g. 200"
            value={filters.responseStatus}
            onChange={value => updateFilter('responseStatus', value)}
          />
        </>
      ),
      filterChips: (Object.keys(FILTER_CHIP_LABELS) as (keyof typeof EMPTY_FILTERS)[])
        .filter(key => appliedFilters[key].trim())
        .map(key => ({
          key,
          label: `${FILTER_CHIP_LABELS[key]} : ${appliedFilters[key].trim()}`,
        })),
      onClearFilterChip: (key: string) => {
        if (key in EMPTY_FILTERS) {
          const next = { ...filters, [key]: '' };
          setFilters(next);
          setAppliedFilters(next);
        }
      },
      onClearAllFilterChips: () => {
        const next = { ...EMPTY_FILTERS };
        setFilters(next);
        setAppliedFilters(next);
      },
      tableColumn: [
        {
          field: 'traceId',
          headerName: 'Trace ID',
          flex: 0.85,
          sortable: false,
          renderCell: (val: string) => (
            <ToolTip title={val || ''}>
              <Typography component="span" variant="body2" noWrap sx={{ maxWidth: 140, display: 'block' }}>
                {truncate(val, 28)}
              </Typography>
            </ToolTip>
          ),
        },
        {
          field: 'method',
          headerName: 'Method',
          flex: 0.45,
          sortable: false,
        },
        {
          field: 'hostname',
          headerName: 'Hostname',
          flex: 0.85,
          sortable: false,
          renderCell: (val: string) => (
            <ToolTip title={val || ''}>
              <Typography component="span" variant="body2" noWrap sx={{ maxWidth: 140, display: 'block' }}>
                {truncate(val, 28)}
              </Typography>
            </ToolTip>
          ),
        },
        {
          field: 'url',
          headerName: 'URL',
          flex: 2,
          sortable: false,
          renderCell: (val: string) => (
            <ToolTip title={val || ''}>
              <Typography component="span" variant="body2" sx={{ maxWidth: 360, display: 'block' }}>
                {truncate(val, 100)}
              </Typography>
            </ToolTip>
          ),
        },
        {
          field: 'responseStatus',
          headerName: 'Status',
          flex: 0.55,
          sortable: false,
          renderCell: (val: unknown) => {
            const code = parseHttpStatus(val);
            const label = val != null && val !== '' ? String(val) : '—';
            if (code == null) {
              const isEmpty = label === '—';
              return (
                <Chip
                  size="small"
                  label={label}
                  variant="outlined"
                  color={isEmpty ? 'default' : 'error'}
                  sx={{ fontWeight: 600 }}
                />
              );
            }
            const ok = code >= 200 && code < 300;
            return <Chip size="small" label={label} color={ok ? 'success' : 'error'} sx={{ fontWeight: 600 }} />;
          },
        },
        {
          field: 'durationMs',
          headerName: 'Duration (ms)',
          flex: 0.6,
          sortable: false,
          renderCell: (val: number | null | undefined) => (
            <Typography component="span" variant="body2">
              {val != null ? String(val) : '—'}
            </Typography>
          ),
        },
        {
          field: 'payloadActions',
          headerName: 'Bodies',
          flex: 1,
          sortable: false,
          renderCell: (_: unknown, row: Record<string, unknown>) => {
            const hasReq = row.requestBody != null && String(row.requestBody).trim() !== '';
            const hasRes = row.responseBody != null && String(row.responseBody).trim() !== '';
            return (
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap onClick={e => e.stopPropagation()}>
                <ToolTip title={hasReq ? 'View request body' : 'No request body'}>
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={!hasReq}
                      sx={{ textTransform: 'none', minWidth: 0, px: 1, py: 0.25, fontSize: '0.75rem' }}
                      onClick={() => openBodyDialog('Request body', row.requestBody as string)}>
                      Request
                    </Button>
                  </span>
                </ToolTip>
                <ToolTip title={hasRes ? 'View response body' : 'No response body'}>
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={!hasRes}
                      sx={{ textTransform: 'none', minWidth: 0, px: 1, py: 0.25, fontSize: '0.75rem' }}
                      onClick={() => openBodyDialog('Response body', row.responseBody as string)}>
                      Response
                    </Button>
                  </span>
                </ToolTip>
              </Stack>
            );
          },
        },
      ],
    }),
    [appliedFilters, filters],
  );

  const getList = useCallback(
    async (filterData: PageProps) => {
      const body = filterData || {
        page: index + 1,
        size: pageSize,
        sort: ['createdAt:desc'],
      };
      try {
        setLoading(true);
        const { data } = await axiosInstance.post(URLS.fetchMediatorLogs(OPERA_LOG_TARGET), {
          ...body,
          filter: buildCicAndFilter(appliedFilters),
        });
        const rows = (data?.content || []).map((item: any, i: number) => ({
          ...item,
          id: [item.traceId, item.id, i].filter(v => v != null && v !== '').join('-') || i,
        }));
        setPageResponse({ ...data, content: rows });
      } catch (error: any) {
        setPageResponse(error);
      } finally {
        setLoading(false);
      }
    },
    [appliedFilters, index, pageSize],
  );

  useEffect(() => {
    if (!canViewOperaLogs) return;
    getList(null as any);
  }, [getList, canViewOperaLogs]);

  if (!canViewOperaLogs) {
    return <Navigate to={routes.dashboard} replace />;
  }

  return (
    <Box>
      <ListPage isLoading={loading} pageConfig={pageConfig} pageResponse={pageResponse} getList={getList} isWhiteSpace={false} />
      <Dialog open={bodyDialog.open} onClose={closeBodyDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pr: 6 }}>
          {bodyDialog.title}
          <IconButton
            aria-label="Copy to clipboard"
            onClick={copyBodyText}
            disabled={!bodyDialog.text || bodyDialog.text === '(empty)'}
            sx={{ position: 'absolute', right: 8, top: 8 }}
            size="small">
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            maxHeight: '70vh',
            bgcolor: 'grey.50',
          }}>
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 1,
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
              fontSize: '0.8rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}>
            {bodyDialog.text}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeBodyDialog} variant="contained" sx={{ textTransform: 'capitalize' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OperaLogsList;
